import React, { useEffect, useMemo, useState } from "react";
import {
  useGeneralRecordedAssessment,
  useStudentNameValuePair,
} from "../../hooks/useGeneralAssessment";
import { Spinner } from "reactstrap";
import { useStudents } from "../../hooks/useOptionalSubjects";
import DefaultImg from "../../assets/images/student.png";
import moment from "moment";
import { useAssessmentScale } from "../../hooks/useAssessmentScale";
import Select from "../shared/Select";
import { components } from "react-select";
import { checkWidgetAdminOrNot, URL } from "../../../../utils/Constants";
import Button from "../shared/Button";
import CustomModal from "../shared/CustomModal";
import CommentBankModal from "./CommentBankModal";
import { useLoggedInRecruitmentId } from "../../hooks/usePublishAssessment";
import { useARRCode } from "../../context/ARRLevelContext";

const AssessmentConfigModal = ({
  assessmentDetail,
  recordedAssessment,
  recordedAssessmentLoading,
  saveGeneralRecordedAssessment,
  selectGeneralRecordedAssessment,
  admissionId,
  finalized,
  closeModal,
  refetchFinalized,
  widgetName,
}) => {
  const { arrLevel } = useARRCode();
  const { assessmentScale } = useAssessmentScale({
    assessmentScaleTypeCode: "sct-lvl",
  });
  const { loggedInRecruitmentId } = useLoggedInRecruitmentId();
  const { students, getAllStudents } = useStudents();
  const { tagsValue, getStudentNameValuePair } = useStudentNameValuePair();

  const [commentBankModal, setCommentBankModal] = useState(false);
  const [selectedCommentId, setSelectedCommentId] = useState(null);

  const [generalConfig, setGeneralConfig] = useState([]);
  const [rubricsConfig, setRubricsConfig] = useState([]);
  const [commentsConfig, setCommentsConfig] = useState([]);

  //assessmentscale config to extract data faster
  const assesmentScaleConfig = useMemo(() => {
    const map = {};

    if (assessmentScale?.length) {
      assessmentScale.forEach((item) => {
        map[item.id] = item;
      });
    }

    return map;
  }, [assessmentScale]);

  //custom Select option style
  const CustomOption = (props) => {
    const { data } = props;

    return (
      <components.Option {...props}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
          }}
        >
          {data.levelType === "image" ? (
            <img
              src={URL.imageSelectURL + data.label}
              style={{ width: 20, marginRight: 10 }}
            />
          ) : (
            <span>{data.label}</span>
          )}
        </div>
      </components.Option>
    );
  };

  //custom Select display value style
  const CustomSingleValue = (props) => {
    const { data } = props;

    return (
      <components.SingleValue {...props}>
        <div style={{ display: "flex", alignItems: "center" }}>
          {data.levelType === "image" ? (
            <img
              src={URL.imageSelectURL + data.label}
              alt=""
              style={{
                width: 20,
                height: 20,
                objectFit: "cover",
                marginRight: 10,
                borderRadius: "50%",
              }}
            />
          ) : (
            <span>{data.label}</span>
          )}
        </div>
      </components.SingleValue>
    );
  };

  //check permission for select input
  const isSelectDisabled = (configId, type) => {
    if (finalized) return true;

    if (checkWidgetAdminOrNot(widgetName)) return false;

    if (!loggedInRecruitmentId) return true;

    if (type === "overallScaleId") {
      const config = generalConfig?.find(
        (item) =>
          item?.assessmentGeneralCustomAssessmentConfigurationId === configId,
      );

      if (!config.hasRole) {
        return parseInt(config.accessor) !== parseInt(loggedInRecruitmentId.id);
      }
      return !(
        config.recruitmentIds?.length !== 0 &&
        config.recruitmentIds.includes(loggedInRecruitmentId?.id)
      );
    }

    if (type === "rubricsScaleId") {
      const config = rubricsConfig?.find(
        (item) =>
          item?.assessmentGeneralCustomAssessmentConfigurationId === configId,
      );
      if (!config.hasRole) {
        return parseInt(config.accessor) !== parseInt(loggedInRecruitmentId.id);
      }
      return !(
        config.recruitmentIds?.length !== 0 &&
        config.recruitmentIds.includes(loggedInRecruitmentId?.id)
      );
    }
  };

  //check if comment is disbaled
  const isCommentDisabled = (comment) => {
    if (finalized) return true;

    if (checkWidgetAdminOrNot(widgetName)) return false;

    if (!loggedInRecruitmentId) return true;

    const recruitmentId = parseInt(loggedInRecruitmentId?.id);
    const commentorId = parseInt(comment.commentor);

    if (!comment.hasRole) {
      return commentorId !== recruitmentId;
    }

    return !(
      comment.recruitmentIds?.length !== 0 &&
      comment.recruitmentIds.includes(loggedInRecruitmentId?.id)
    );
  };

  //render assessment scale levels in select input in table
  const renderAssessmentScaleLevel = (configId, scaleId, type, rubricId) => {
    const scale = assesmentScaleConfig[scaleId];
    const scaleOptions = scale?.assessmentScaleLevels?.map((item) => ({
      label: item?.level,
      value: item?.id,
      levelType: item?.levelType,
    }));

    let config;

    if (type === "overallScaleId") {
      config = generalConfig?.find(
        (item) =>
          item?.assessmentGeneralCustomAssessmentConfigurationId === configId,
      );
    } else {
      config = rubricsConfig?.find(
        (item) =>
          item?.assessmentGeneralCustomAssessmentConfigurationId === configId &&
          item?.assessmentGeneralCustomAssessmentRubricsConfigurationid ===
            rubricId,
      );
    }

    return (
      <Select
        value={
          scaleOptions?.find(
            (opt) => opt.value === config?.assessmentScaleLevelId,
          ) || null
        }
        options={scaleOptions}
        styles={{ control: { minWidth: "200px" } }}
        onChange={(option) =>
          handleGeneralAssessmentConfigChange(
            configId,
            "assessmentScaleLevelId",
            option?.value,
            type,
            rubricId,
          )
        }
        isClearable={false}
        isDisabled={isSelectDisabled(configId, type)}
        components={{ Option: CustomOption, SingleValue: CustomSingleValue }}
      />
    );
  };

  //convert html tags to plain text
  const handleConvertToPlainText = (content) => {
    if (typeof content !== "string") return "";

    const temp = document.createElement("div");
    temp.innerHTML = content;

    const plainText = temp.textContent || temp.innerText || "";
    return plainText;
  };

  const handleGeneralAssessmentConfigChange = (
    id,
    field,
    value,
    type,
    rubricId,
  ) => {
    if (type === "overallScaleId") {
      const updated = generalConfig?.map((item) => {
        if (item?.assessmentGeneralCustomAssessmentConfigurationId !== id)
          return item;

        return {
          ...item,
          [field]: value,
        };
      });

      setGeneralConfig(updated);
    } else {
      const updated = rubricsConfig?.map((item) => {
        if (
          item?.assessmentGeneralCustomAssessmentConfigurationId !== id ||
          item?.assessmentGeneralCustomAssessmentRubricsConfigurationid !==
            rubricId
        )
          return item;

        return {
          ...item,
          [field]: value,
        };
      });

      setRubricsConfig(updated);
    }
  };

  //comment change handler
  const handleCommentChange = (e, commentId) => {
    const { name, value } = e.target;

    setCommentsConfig((prev) => {
      const updated = prev?.map((item) => {
        if (item?.assessmentGeneralCommentConfigurationId !== commentId)
          return item;
        return {
          ...item,
          [name]: value,
        };
      });

      return updated;
    });
  };

  //save assessment records handler
  const handleSaveAssessmentRecords = async () => {
    if (!assessmentDetail) return;

    const generalAssessment = generalConfig
      ?.filter(
        (item) =>
          item?.assessmentScaleLevelId != null &&
          item?.assessmentScaleId !== null,
      )
      ?.map((item) => ({
        assessmentScaleLevelId: item?.assessmentScaleLevelId,
        assessmentGeneralCustomAssessmentConfigurationId:
          item?.assessmentGeneralCustomAssessmentConfigurationId,
        assessmentScaleId: item?.assessmentScaleId,
      }));

    const rubricsAssessment = rubricsConfig
      ?.filter(
        (item) =>
          item?.assessmentScaleLevelId != null &&
          item?.assessmentScaleId !== null,
      )
      ?.map((item) => ({
        assessmentScaleLevelId: item?.assessmentScaleLevelId,
        assessmentGeneralCustomAssessmentConfigurationId:
          item?.assessmentGeneralCustomAssessmentConfigurationId,
        assessmentGeneralCustomAssessmentRubricsConfigurationid:
          item?.assessmentGeneralCustomAssessmentRubricsConfigurationid,
        assessmentScaleId: item?.assessmentScaleId,
      }));

    const comments = commentsConfig
      ?.filter((item) => item?.commentTextValue?.trim())
      ?.map((item) => ({
        commentTextValue: item?.commentTextValue,
        assessmentGeneralCommentConfigurationId:
          item?.assessmentGeneralCommentConfigurationId,
      }));

    const payload = {
      assessmentId: assessmentDetail.assessmentId,
      classroomId: assessmentDetail.classroomId,
      yearLevelId: assessmentDetail.yearLevelId,
      admissionId,
      studentGeneralAssessmentComments: comments,
      studentGeneralAssessmentCustomAssessmentRubricsScales: rubricsAssessment,
      studentGeneralAssessmentCustomAssessmentScales: generalAssessment,
    };

    try {
      await saveGeneralRecordedAssessment(payload);

      const params = {
        ...assessmentDetail,
        admissionId,
      };
      selectGeneralRecordedAssessment(params);

      const finalizedParams = {
        assessmentId: payload?.assessmentId,
        classroomId: payload?.classroomId,
        yearLevelId: payload?.yearLevelId,
      };
      refetchFinalized(finalizedParams);
    } catch (error) {
      console.error("Save Failed: ", error);
    }
  };

  //get students
  useEffect(() => {
    const params = {
      assessmentId: assessmentDetail?.assessmentId,
      moduleCode: arrLevel,
      yearLevelId: assessmentDetail?.yearLevelId,
      classroomId: assessmentDetail?.classroomId,
      admissionId: admissionId,
    };

    getAllStudents(params);
  }, [assessmentDetail, admissionId]);

  //populate general config and comments
  useEffect(() => {
    if (recordedAssessment?.generalConfig?.length > 0) {
      const allGeneralConfig = recordedAssessment.generalConfig.map((item) => ({
        ...item,
        assessmentGeneralCustomAssessmentConfigurationId: item.id,
        assessmentScaleId: item?.overallScaleId ?? null,
        assessmentScaleLevelId:
          item.overallAchievement?.achievementLevelId ?? null,
      }));

      setGeneralConfig(allGeneralConfig);

      const allRubricsConfig = recordedAssessment.generalConfig.flatMap(
        (item) => {
          if (item?.rubricsScaleId && item?.rubrics?.length > 0) {
            return item?.rubrics?.map((rubric) => ({
              ...rubric,
              assessmentGeneralCustomAssessmentConfigurationId: item.id,
              assessmentGeneralCustomAssessmentRubricsConfigurationid:
                rubric.id,
              assessmentScaleLevelId: rubric.achievementLevelId ?? null,
              assessmentScaleId: item?.rubricsScaleId ?? null,
            }));
          }

          return [];
        },
      );

      setRubricsConfig(allRubricsConfig ?? []);
    }

    if (recordedAssessment?.comments?.length > 0) {
      const allComments = recordedAssessment?.comments.map((comment) => ({
        ...comment,
        commentTextValue: comment?.commentText,
        assessmentGeneralCommentConfigurationId: comment.id,
      }));

      setCommentsConfig(allComments);
    }
  }, [recordedAssessment]);

  return (
    <>
      {recordedAssessmentLoading.read ? (
        <div className="new-spinner-container">
          <div className="new-spinner">
            <div className="loader">
              <Spinner color="primary" />
            </div>
            <div style={{ fontSize: "16px", marginTop: "15px" }}>
              Please wait !! Processing.....
            </div>
          </div>
        </div>
      ) : recordedAssessment?.hasPermission &&
        (recordedAssessment?.generalConfig?.length > 0 ||
          recordedAssessment?.comments?.length > 0) ? (
        <>
          <div style={{ gap: "24px", marginBottom: "20px" }} className="flex">
            <img
              style={{ width: "80px", height: "80px", objectFit: "cover" }}
              src={
                students?.[0]?.photo
                  ? URL.imageSelectURL + students?.[0]?.photo
                  : DefaultImg
              }
              alt="student image"
            />

            <div>
              <p
                style={{
                  marginBottom: "7px",
                  fontSize: "20px",
                  fontWeight: "600",
                }}
              >
                {students?.[0]?.name}
              </p>
              <p style={{ marginBottom: "7px" }}>
                Roll Number: {students?.[0]?.rollNumber ?? "N/A"}
              </p>
              <p style={{ marginBottom: "7px" }}>
                Registration Number:{" "}
                {students?.[0]?.registrationNumber ?? "N/A"}
              </p>
              <p style={{ marginBottom: "7px" }}>
                Symbol Number: {students?.[0]?.symbolNo ?? "N/A"}
              </p>
            </div>
          </div>

          <div
            style={{ gridTemplateColumns: "1fr", gap: "24px" }}
            className="arr-form grid"
          >
            {recordedAssessment?.generalConfig?.map((item) => (
              <div key={item?.id} className="form-input">
                <label
                  style={{ fontWeight: "400", fontSize: "14px" }}
                  htmlFor={item?.header}
                >
                  {item?.header}
                </label>
                {item?.overallScaleId &&
                  renderAssessmentScaleLevel(
                    item?.id,
                    item?.overallScaleId,
                    "overallScaleId",
                  )}

                {item?.rubricsScaleId && (
                  <div
                    style={{ margin: "0" }}
                    className="arr-table-container noShadow"
                  >
                    <table className="arr-table">
                      <thead>
                        <tr>
                          <th>{item?.header} Rubrics</th>
                          <th>Grade</th>
                        </tr>
                      </thead>
                      <tbody>
                        {item?.rubrics?.map((rubric) => (
                          <tr key={rubric?.id}>
                            <td>{rubric.name}</td>
                            <td>
                              {renderAssessmentScaleLevel(
                                item?.id,
                                item?.rubricsScaleId,
                                "rubricsScaleId",
                                rubric?.id,
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ))}
          </div>

          {recordedAssessment?.comments?.length > 0 && (
            <div
              style={{
                gridTemplateColumns: "1fr",
                gap: "24px",
                marginTop: "20px",
              }}
              className="arr-form grid"
            >
              <div className="arr-heading">
                <h2>Comments</h2>
              </div>
              {recordedAssessment?.comments?.map((comment) => {
                const remark = commentsConfig?.find(
                  (c) =>
                    c.assessmentGeneralCommentConfigurationId === comment?.id,
                );

                const commentText = handleConvertToPlainText(
                  remark?.commentTextValue ?? comment?.commentTextValue,
                );

                return (
                  <div key={comment?.id} className="form-input">
                    <label
                      style={{ fontWeight: "400", fontSize: "14px" }}
                      htmlFor={comment?.name}
                    >
                      {comment?.name}
                    </label>
                    <input
                      className="form-control"
                      value={commentText}
                      placeholder="Enter comment"
                      name="commentTextValue"
                      onChange={(e) => handleCommentChange(e, comment?.id)}
                      disabled={finalized || isCommentDisabled(comment)}
                      readOnly={finalized}
                    />
                    <div
                      style={{
                        marginTop: "8px",
                        justifyContent: "space-between",
                      }}
                      className="flex"
                    >
                      <div className="flex" style={{ gap: "12px" }}>
                        <span>Type: {comment?.textType}</span>
                        <span>Min Length: {comment?.minLen}</span>
                        <span>Max Length: {comment?.maxLen}</span>
                      </div>

                      <Button
                        onClick={() => {
                          setCommentBankModal(true);
                          setSelectedCommentId(comment?.id);
                          getStudentNameValuePair(students?.[0]?.peopleId);
                        }}
                        disabled={isCommentDisabled(comment)}
                        size="sm"
                      >
                        Comments Bank
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div style={{ marginTop: "16px" }} className="action-btns end">
            <Button
              onClick={() => {
                closeModal();
              }}
              variant="outline"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveAssessmentRecords}
              disabled={
                finalized ||
                (!checkWidgetAdminOrNot(widgetName) &&
                  !loggedInRecruitmentId?.id) ||
                recordedAssessmentLoading.insert
              }
            >
              {recordedAssessmentLoading.insert ? (
                <Spinner size={"sm"} color="white" />
              ) : (
                "Save Assessment Records"
              )}
            </Button>
          </div>
        </>
      ) : !recordedAssessment?.hasPermission ? (
        <div style={{ textAlign: "center" }} className="empty-data">
          You don't have permission
        </div>
      ) : (
        <div className="empty-data">No Data Found</div>
      )}

      <CustomModal
        size="lg"
        isOpen={commentBankModal}
        toggle={() => setCommentBankModal(false)}
        modalHeader={"Comments Bank"}
        children={
          <CommentBankModal
            tagsValue={tagsValue}
            closeModal={() => setCommentBankModal(false)}
            setCommentsConfig={setCommentsConfig}
            selectedCommentId={selectedCommentId}
            setSelectedCommentId={setSelectedCommentId}
          />
        }
      />
    </>
  );
};

export default AssessmentConfigModal;
